// Excel експортът на „Монитори".
//
// Редовете идват ФИЛТРИРАНИ и ПОДРЕДЕНИ от таблицата (`onFilteredRowsChange`),
// не суровият отговор: файлът повтаря екрана, включително филтрите в колоните.
// БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.
//
// Колоните и стойностите са същите като в стария експорт. Едно изключение:
// „Влязла" излиза като „ДД.ММ.ГГГГ ЧЧ:ММ:СС", а не като суровия ISO низ —
// така клетката се чете без обработка.

import dayjs from "dayjs";

import { parseDecimal } from "../../../../components/dataTable";
import { exportToExcel, type ExcelColumn } from "../../../../utils/excelExport";
import type { KitchenMonitorReportRow } from "../../../../api/reportsKitchenMonitors";
import { formatDuration } from "./kitchenMonitorsFormat";

type Options = {
  rows: readonly KitchenMonitorReportRow[];
  /** i18n ключът на „Добавка". */
  addonKey: string;
  /** i18n ключът на продаваемото — „Асортимент" или „Рецепта" (склад). */
  sellableKey: string;
  t: (key: string) => string;
};

export const exportKitchenMonitors = ({
  rows,
  addonKey,
  sellableKey,
  t,
}: Options) => {
  const columns: ExcelColumn<KitchenMonitorReportRow>[] = [
    {
      header: t("reportsPage.kmTable"),
      value: (row) => row.bill_name,
      width: 14,
    },
    {
      header: t("reportsPage.product"),
      value: (row) => row.item_name,
      width: 28,
    },
    {
      header: t("reportsPage.kmType"),
      value: (row) => t(row.is_addition ? addonKey : sellableKey),
      width: 14,
    },
    {
      header: t("reportsPage.kmStation"),
      value: (row) => row.station_name,
      width: 18,
    },
    // Количеството излиза като ЧИСЛО — Decimal от BE е JSON низ и се парсва
    // през общия `parseDecimal` (празно/счупено → 0, не NaN в клетката).
    {
      header: t("reportsPage.kmQty"),
      value: (row) => parseDecimal(row.quantity),
      width: 8,
    },
    {
      header: t("reportsPage.kmEnteredAt"),
      value: (row) =>
        row.ordered_at ? dayjs(row.ordered_at).format("DD.MM.YYYY HH:mm:ss") : "",
      width: 20,
    },
    // Времената излизат ФОРМАТИРАНИ („4m 05s"), както на екрана и както в
    // стария файл; липсващият статус остава тире.
    {
      header: t("reportsPage.kmStatusInProgress"),
      value: (row) => formatDuration(row.seconds_to_start),
      width: 12,
    },
    {
      header: t("reportsPage.kmStatusCooked"),
      value: (row) => formatDuration(row.seconds_cooking),
      width: 12,
    },
    {
      header: t("reportsPage.kmStatusIssued"),
      value: (row) => formatDuration(row.seconds_to_issue),
      width: 12,
    },
    {
      header: t("reportsPage.kmStatusServed"),
      value: (row) => formatDuration(row.seconds_to_serve),
      width: 12,
    },
    {
      header: t("reportsPage.kmStartedBy"),
      value: (row) => row.started_by ?? "",
      width: 18,
    },
    {
      header: t("reportsPage.kmCookedBy"),
      value: (row) => row.cooked_by ?? "",
      width: 18,
    },
    {
      header: t("reportsPage.kmIssuedBy"),
      value: (row) => row.issued_by ?? "",
      width: 18,
    },
    {
      header: t("reportsPage.kmServedBy"),
      value: (row) => row.served_by ?? "",
      width: 18,
    },
  ];

  exportToExcel<KitchenMonitorReportRow>({
    filename: t("reportsPage.kitchenMonitors"),
    sheetName: t("reportsPage.kitchenMonitors"),
    columns,
    rows: [...rows],
  });
};
